import styled, { css } from 'styled-components';

import Box from '../../../../_shared/components/Box';
import Badge from '../../../../_shared/components/Badge';
import Label from '../../../../_shared/components/Label';
import Detail from '../../../../_shared/components/Detail';
import Button from '../../../../_shared/components/Button';
import applyResponsiveVdpStyles from '../../../../_shared/helpers/applyResponsiveVdpStyles';
import isOmpPlatform from '../../../../_shared/utils/isOmpPlatform';

const isOmp = isOmpPlatform();

/**
 * Variant Definitions
 */
const styleTextEllipsis = css`
  text-overflow: ellipsis;
  white-space: nowrap;
  overflow: hidden;
`;

const StyledVehicleInfoVariants = {
  mini: css`
    padding: 24px 18px 0 18px;
  `,
  expanded: css`
    padding: ${isOmp ? '26px 36px 26px 36px' : '26px 36px 36px 36px'};
    background-color: ${({ theme }) => theme.COLORS.BASE};
    max-width: ${({ isSelling }) => (isSelling ? '568px;' : 'unset;')};
  `
};

const StyledBoxVariants = {
  mini: css`
    flex-direction: row;
    font-size: ${({ theme }) => theme.FONT_SIZES.LARGE};
    margin-bottom: 20px;
  `,
  expanded: css`
    font-size: 42px;
    margin-bottom: 26px;

    &:nth-child(1) {
      margin-bottom: 20px;
    }

    &:nth-child(2) {
      margin-bottom: 21px;
    }

    &:nth-child(3) {
      margin-bottom: 0;
      justify-content: space-between;
    }
  `
};

const YearVariants = {
  mini: css`
    font-size: ${({ theme }) => theme.FONT_SIZES.LARGE};
    ${isOmp &&
      css`
        height: 29px;
        line-height: 28px;
      `}
  `,
  expanded: css`
    font-size: 25px;
  `
};

const MakeModelVariants = {
  mini: css`
    font-size: ${({ theme }) => theme.FONT_SIZES.LARGE};
  `,
  expanded: css`
    font-size: ${({ theme }) => theme.FONT_SIZES.XXLARGE};
  `
};

const VinWebVariants = {
  mini: css`
    display: none;
  `
};

const VinMobileVariants = {
  expanded: css`
    display: none;
  `
};

const StyledDetailVariants = {
  mini: css`
    padding: 0 32px 18px 0;
  `
};

/**
 * Styled Components
 */
export const StyledVehicleInfo = styled.div`
  display: flex;
  width: 100%;
  flex-direction: column;
  ${({ theme }) =>
    applyResponsiveVdpStyles(theme, {
      mini: StyledVehicleInfoVariants.mini,
      expanded: StyledVehicleInfoVariants.expanded
    })}
`;

export const StyledBox = styled(Box)`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  width: 100%;

  &:first-child {
    flex-wrap: nowrap;
  }

  ${({ theme }) =>
    applyResponsiveVdpStyles(theme, {
      mini: StyledBoxVariants.mini,
      expanded: StyledBoxVariants.expanded
    })}

  &:last-child {
    margin-bottom: 0;
  }
`;

// TODO: Overwrite from new Badge
export const Year = styled(Badge)`
  border: 2px solid ${({ theme }) => theme.COLORS.BASE_INVERTED};
  font-family: ${({ theme }) => theme.FONT.SEMI_BOLD};
  color: ${({ theme }) => theme.COLORS.BASE_INVERTED};
  padding: 0 5px;
  line-height: 1;
  border-radius: 5.5px;
  ${({ theme }) =>
    applyResponsiveVdpStyles(theme, {
      mini: YearVariants.mini,
      expanded: YearVariants.expanded
    })}
`;

export const Make = styled(Label.Light)`
  white-space: nowrap;
  display: inline-block;
  padding-left: 12px;
  padding-right: 6px;
  vertical-align: middle;
  color: ${({ theme }) => theme.COLORS.BASE_INVERTED};
  ${({ theme }) =>
    applyResponsiveVdpStyles(theme, {
      mini: MakeModelVariants.mini,
      expanded: MakeModelVariants.expanded
    })}
`;

export const Model = styled(Label.Bold)`
  ${styleTextEllipsis}
  display: block;
  color: ${({ theme }) => theme.COLORS.BASE_INVERTED};
  font-size: ${({ theme }) => theme.FONT_SIZES.XXLARGE};
  ${({ theme }) =>
    applyResponsiveVdpStyles(theme, {
      mini: MakeModelVariants.mini,
      expanded: MakeModelVariants.expanded
    })}
`;

export const AsIsContainer = styled.div`
  display: flex;
`;

// TODO: Overwrite from new Badge
export const AsIs = styled(Badge)`
  background-color: ${({ theme }) => theme.COLORS.ORANGE};
  border-radius: 18px;
  margin-right: 10px;
  font-family: ${({ theme }) => theme.FONT.SEMI_BOLD};
  font-size: ${({ theme }) => theme.FONT_SIZES.BASE};
  text-align: center;
  height: ${isOmp ? '26px' : '24px'};
  min-width: 52px;
  padding: 0px 6px;
`;

export const TrimVin = styled.div`
  color: ${({ theme }) => theme.COLORS.NEUTRAL_30};
  font-size: ${({ theme }) => theme.FONT_SIZES.BASE};
  font-family: ${({ theme }) => theme.FONT.REGULAR};
  display: ${props => {
    if (props.displayInherit) {
      return 'inherit';
    }
    if (isOmp) {
      return 'flex';
    }
    return '';
  }};
`;

export const Trim = styled.div`
  margin-bottom: 5px;
  font-family: ${({ theme }) => theme.FONT.REGULAR};
  display: inherit;
`;

export const VinText = styled.span`
  color: ${({ theme }) => theme.COLORS.TERTIARY};
  text-decoration: ${isOmp ? 'none' : 'underline'};
  text-transform: initial;
`;

const Vin = styled(Label)`
  margin-left: 4px;
  font-size: ${({ theme }) => theme.FONT_SIZES.BASE};
  color: ${({ theme }) => theme.COLORS.NEUTRAL_30};
  line-height: 21px;
  cursor: initial;
`;

Vin.Web = styled.span`
  ${({ theme }) =>
    applyResponsiveVdpStyles(theme, {
      mini: VinWebVariants.mini
    })}
`;

Vin.Mobile = styled.span`
  ${({ theme }) =>
    applyResponsiveVdpStyles(theme, {
      expanded: VinMobileVariants.expanded
    })}
`;

export { Vin };

export const StyledModalButton = styled(Button)`
  border: none;
  outline: none;
  padding: 0;
  font-family: ${({ theme }) => theme.FONT.REGULAR};
  font-size: ${({ theme }) => theme.FONT_SIZES.BASE};
  letter-spacing: normal;
  background-color: ${({ theme }) => theme.COLORS.BASE};
  text-transform: inherit;
  text-decoration: ${isOmp ? 'none' : 'underline'};
  margin: 0;
  color: ${({ theme }) => theme.COLORS.TERTIARY};
  cursor: pointer;
`;

export const StyledDetail = styled(Detail)`
  height: 60px;
  &:not(:last-child) {
    padding-right: 31px;
  }
  ${({ theme }) =>
    applyResponsiveVdpStyles(theme, {
      mini: StyledDetailVariants.mini
    })}
`;
